import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DescricaoPrioridade, PrioridadeEnum } from 'src/app/enum/prioridade.enum';
import { DescricaoStatusTicket, StatusTicketEnum } from 'src/app/enum/statusTicket.enum';
import { DescricaoTipoTicket, TipoTicketEnum } from 'src/app/enum/tipoTicket.enum';
import { ImagemBase64 } from 'src/app/model/imagemBase64';
import { ClientService } from 'src/app/service/client.service';
import { SegmentoService } from 'src/app/service/segmento.service';
import { TicketService } from 'src/app/service/ticket.service';
import { Utils } from 'src/app/util/utils';
import { Client } from '../client/client';
import { Segmento } from '../segmento/segmento';
import { Usuario } from '../usuario/usuario';
import { NotaTicket } from './nota';
import { Ticket } from './ticket';

interface UploadedFile {
  name: string;
  preview: SafeUrl;
  file: File;
}

@Component({
  selector: 'app-ticket-form',
  templateUrl: './ticket-form.component.html',
  styleUrls: ['./ticket.component.scss'],
})
export class TicketFormComponent implements OnInit, OnDestroy {

  formulario: FormGroup;
  segmentos: Segmento[] = [];
  cronometroInicio: Date | null = null;
  tempoGastoSegundos = 0;
  agora = Date.now();
  private tickHandle: any = null;

  cabecalhoBloqueado = false;
  tempoEstimadoBloqueado = false;

  @ViewChild('dialogAlteracao') dialogAlteracao!: TemplateRef<any>;
  private dialogRef: MatDialogRef<any> | null = null;
  readonly camposAlteraveis = [
    { id: 'STATUS', label: 'Status' },
    { id: 'CATEGORIA', label: 'Categoria' },
    { id: 'PREVISAO', label: 'Previsão de Entrega' },
    { id: 'RESPONSAVEL', label: 'Atribuído a' },
  ];
  alteracaoCampo: string | null = null;
  alteracaoStatus: any = null;
  alteracaoCategoria: any = null;
  alteracaoPrevisao: Date | null = null;
  alteracaoResponsavel: any = null;
  alteracaoMotivo = '';
  salvandoAlteracao = false;

  statusLista = Object.values(StatusTicketEnum);
  tipoLista = Object.values(TipoTicketEnum);
  descStatus = (v: any) => DescricaoStatusTicket.get(v) || (v ?? '—');
  descTipo = (v: any) => DescricaoTipoTicket.get(v) || (v ?? '—');
  uploadedFiles: UploadedFile[] = [];
  isDragOver = false;
  novoComentario = '';
  comentarios: NotaTicket[] = [];
  atribuicoes: Usuario[] = [];
  base64Files: ImagemBase64[] = [];
  page = 0;
  size = 5;
  lastPage = false;
  loading = false;
  imagensSelecionadas: ImagemBase64[] = [];
  parceiros: Client[] = [];
  compararCampos = this.utils.compararCampos;
  carregando = false;

  readonly INTERNO = { id: -1, razaoSocial: 'Interno' };

  constructor(
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private utils: Utils,
    private ticketService: TicketService,
    private segmentoService: SegmentoService,
    private sanitizer: DomSanitizer,
    private clientService: ClientService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.montarFormulario();
    this.buscarUsuariosAtribuicao();
    this.consultarParceiros();
    this.carregarSegmentos();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.editar(+id);
    }
  }

  ngOnDestroy(): void {
    this.pararTick();
  }

  montarFormulario() {
    this.formulario = this.formBuilder.group({
      id: [null],
      parceiro: [undefined],
      dataCriacao: [null],
      usuario: [localStorage.getItem('usuarioLogado')],
      abertoPor: [localStorage.getItem('usuarioLogado')],
      menu: [null],
      subMenu: [null],
      tipoTicket: [null],
      segmento: [null],
      prioridade: [null],
      status: [StatusTicketEnum.BACKLOG],
      apresentarParceiro: [null],
      responsavelSuporte: [null],
      responsavelDesenvolvimento: [null],
      nota: [null],
      dataPrevisaoEntrega: [null],
      dataConclusaoEntrega: [null],
      dataConfirmacaoEntrega: [null],
      dataFinalizacao: [null],
      texto: [null],
      tempoEstimadoHoras: [null],
      apresentarParceiroTicket: [false],
      apresentarParceiroInteracao: [false],
    });
  }

  carregarSegmentos() {
    this.segmentoService.listarAtivos().subscribe(lista => {
      this.segmentos = lista ?? [];
    });
  }

  submit() {
    const formValues = this.formulario.getRawValue();
    const ticket: Ticket = new Ticket(
      formValues.id,
      formValues.parceiro?.id === -1 ? null : formValues.parceiro,
      formValues.dataCriacao,
      formValues.usuario?.toUpperCase(),
      formValues.menu,
      formValues.subMenu,
      formValues.tipoTicket,
      formValues.segmento,
      formValues.prioridade,
      formValues.status,
      formValues.apresentarParceiroTicket,
      formValues.responsavelSuporte,
      formValues.responsavelDesenvolvimento,
      this.comentarios,
      formValues.dataPrevisaoEntrega,
      formValues.dataConclusaoEntrega,
      formValues.dataConfirmacaoEntrega,
      formValues.dataFinalizacao,
      formValues.texto,
      this.base64Files
    );
    ticket.abertoPor = formValues.abertoPor || localStorage.getItem('usuarioLogado');
    ticket.tempoEstimadoMinutos = this.calcularTempoEstimadoMinutos();
    ticket.tempoGastoSegundos = this.tempoGastoSegundos;
    ticket.cronometroInicio = this.cronometroInicio;

    const request$ = formValues.id
      ? this.ticketService.update(ticket)
      : this.ticketService.save(ticket);

    request$.subscribe({
      next: (resposta: Ticket) => {
        this.snackBar.open(
          formValues.id ? 'Ticket atualizado com sucesso!' : 'Ticket salvo com sucesso!',
          'Fechar',
          { duration: 3000 }
        );
        if (resposta.id) {
          this.router.navigate(['/ticket/form', resposta.id]);
          this.editar(resposta.id);
        }
      },
      error: () => this.snackBar.open('Erro ao salvar ticket', 'Fechar', { duration: 3000 })
    });
  }

  editar(id: number) {
    this.carregando = true;
    this.ticketService.getById(id).subscribe({
      next: (response) => {
        this.formulario.patchValue({
          id: response.id,
          parceiro: response.parceiro === null ? this.INTERNO : response.parceiro,
          dataCriacao: response.createdAt,
          usuario: response.usuario,
          abertoPor: response.abertoPor ?? response.usuario,
          menu: response.menu,
          subMenu: response.subMenu,
          prioridade: response.prioridade,
          tipoTicket: response.tipo,
          segmento: response.segmento,
          status: response.status,
          apresentarParceiro: response.apresentarParceiro,
          responsavelSuporte: response.responsavelSuporte,
          responsavelDesenvolvimento: response.responsavelDesenvolvimento,
          nota: response.nota,
          dataPrevisaoEntrega: response.dataPrevisaoEntrega,
          dataConclusaoEntrega: response.dataConclusaoEntrega,
          dataConfirmacaoEntrega: response.dataConfirmacaoEntrega,
          dataFinalizacao: response.dataFinalizacao,
          texto: response.texto,
          tempoEstimadoHoras: this.minutosParaHoras(response.tempoEstimadoMinutos),
          apresentarParceiroTicket: response.apresentarParceiro,
        });
        this.tempoEstimadoBloqueado = response.tempoEstimadoBloqueado === true;
        this.aplicarTempos(response);
        this.bloquearCabecalho();
        this.base64Files = response.imagensBase64 || [];
        this.uploadedFiles = this.base64Files.map(img => ({
          name: img.nome,
          preview: this.sanitizer.bypassSecurityTrustUrl(img.base64),
          file: new File([], img.nome)
        }));
        this.carregarComentarios(id, true);
        this.carregando = false;
      },
      error: () => {
        this.carregando = false;
        this.snackBar.open('Ticket não encontrado.', 'ERRO', { duration: 2500 });
        this.voltarConsulta();
      }
    });
  }

  limparFormulario() {
    this.router.navigate(['/ticket/form']);
    this.formulario.reset({
      status: StatusTicketEnum.BACKLOG,
      usuario: localStorage.getItem('usuarioLogado'),
      abertoPor: localStorage.getItem('usuarioLogado'),
    });
    this.uploadedFiles = [];
    this.base64Files = [];
    this.comentarios = [];
    this.tempoGastoSegundos = 0;
    this.cronometroInicio = null;
    this.tempoEstimadoBloqueado = false;
    this.pararTick();
    this.desbloquearCabecalho();
  }

  // ── Bloqueio do cabeçalho após salvar ─────────────────────────────────────────
  private readonly camposCabecalho = [
    'segmento', 'parceiro', 'usuario', 'prioridade', 'dataCriacao',
    'tipoTicket', 'dataPrevisaoEntrega', 'responsavelSuporte', 'status',
  ];

  private bloquearCabecalho(): void {
    this.cabecalhoBloqueado = true;
    this.camposCabecalho.forEach(nome =>
      this.formulario.get(nome)?.disable({ emitEvent: false }));
  }

  private desbloquearCabecalho(): void {
    this.cabecalhoBloqueado = false;
    this.camposCabecalho.forEach(nome =>
      this.formulario.get(nome)?.enable({ emitEvent: false }));
  }

  // ── Alteração controlada (modal + motivo) ─────────────────────────────────────
  abrirAlteracao(): void {
    if (!this.ticketId) {
      this.snackBar.open('Salve o ticket antes de alterar.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    const raw = this.formulario.getRawValue();
    this.alteracaoCampo = 'STATUS';
    this.alteracaoStatus = raw.status;
    this.alteracaoCategoria = raw.tipoTicket;
    this.alteracaoPrevisao = raw.dataPrevisaoEntrega ? new Date(raw.dataPrevisaoEntrega) : null;
    this.alteracaoResponsavel = raw.responsavelSuporte ?? null;
    this.alteracaoMotivo = '';
    this.dialogRef = this.dialog.open(this.dialogAlteracao, { width: '480px' });
  }

  confirmarAlteracao(): void {
    if (!this.alteracaoCampo) {
      return;
    }
    if (!this.alteracaoMotivo?.trim()) {
      this.snackBar.open('Informe o motivo da alteração.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    const id = this.ticketId!;
    const raw = this.formulario.getRawValue();
    const campoLabel = this.camposAlteraveis.find(c => c.id === this.alteracaoCampo)?.label;
    let controlName = '';
    let novoValor: any;
    let anteriorLabel = '';
    let novoLabel = '';

    switch (this.alteracaoCampo) {
      case 'STATUS':
        controlName = 'status';
        novoValor = this.alteracaoStatus;
        anteriorLabel = this.descStatus(raw.status);
        novoLabel = this.descStatus(novoValor);
        break;
      case 'CATEGORIA':
        controlName = 'tipoTicket';
        novoValor = this.alteracaoCategoria;
        anteriorLabel = this.descTipo(raw.tipoTicket);
        novoLabel = this.descTipo(novoValor);
        break;
      case 'PREVISAO':
        controlName = 'dataPrevisaoEntrega';
        novoValor = this.alteracaoPrevisao;
        anteriorLabel = this.formatarDataBr(raw.dataPrevisaoEntrega);
        novoLabel = this.formatarDataBr(novoValor);
        break;
      case 'RESPONSAVEL':
        controlName = 'responsavelSuporte';
        novoValor = this.alteracaoResponsavel;
        anteriorLabel = raw.responsavelSuporte?.name ?? '—';
        novoLabel = novoValor?.name ?? '—';
        break;
    }

    if (novoValor === null || novoValor === undefined || novoValor === '') {
      this.snackBar.open('Informe o novo valor.', 'ATENÇÃO', { duration: 3000 });
      return;
    }

    this.formulario.get(controlName)?.patchValue(novoValor, { emitEvent: false });
    const formValues = this.formulario.getRawValue();
    const ticket: Ticket = new Ticket(
      formValues.id,
      formValues.parceiro?.id === -1 ? null : formValues.parceiro,
      formValues.dataCriacao,
      formValues.usuario?.toUpperCase(),
      formValues.menu,
      formValues.subMenu,
      formValues.tipoTicket,
      formValues.segmento,
      formValues.prioridade,
      formValues.status,
      formValues.apresentarParceiroTicket,
      formValues.responsavelSuporte,
      formValues.responsavelDesenvolvimento,
      this.comentarios,
      formValues.dataPrevisaoEntrega,
      formValues.dataConclusaoEntrega,
      formValues.dataConfirmacaoEntrega,
      formValues.dataFinalizacao,
      formValues.texto,
      this.base64Files
    );
    ticket.abertoPor = formValues.abertoPor;
    ticket.tempoEstimadoMinutos = this.calcularTempoEstimadoMinutos();
    ticket.tempoGastoSegundos = this.tempoGastoSegundos;
    ticket.cronometroInicio = this.cronometroInicio;

    const texto = `🔧 ${campoLabel} alterado de "${anteriorLabel}" para "${novoLabel}".\nMotivo: ${this.alteracaoMotivo.trim()}`;
    this.salvandoAlteracao = true;
    this.ticketService.update(ticket).subscribe({
      next: () => {
        this.registrarInteracao(texto, () => {
          this.salvandoAlteracao = false;
          this.dialogRef?.close();
          this.editar(id);
          this.snackBar.open('Alteração registrada.', 'OK', { duration: 2500 });
        });
      },
      error: () => {
        this.salvandoAlteracao = false;
        this.snackBar.open('Erro ao registrar alteração.', 'ERRO', { duration: 3000 });
      }
    });
  }

  private registrarInteracao(texto: string, aoConcluir?: () => void): void {
    const id = this.ticketId;
    if (!id) {
      aoConcluir?.();
      return;
    }
    const nota = new NotaTicket(
      0, texto, null,
      localStorage.getItem('usuarioLogado') || 'Sistema',
      id, false, []
    );
    this.ticketService.adicionarComentario(id, nota, 0, this.size).subscribe({
      next: () => {
        this.carregarComentarios(id, true);
        aoConcluir?.();
      },
      error: () => {
        this.snackBar.open('Erro ao registrar interação.', 'Fechar', { duration: 3000 });
        aoConcluir?.();
      }
    });
  }

  private formatarDataBr(valor: any): string {
    if (!valor) {
      return '—';
    }
    const d = new Date(valor);
    if (isNaN(d.getTime())) {
      return '—';
    }
    return d.toLocaleDateString('pt-BR');
  }

  // ── Cronômetro / tempo da demanda ─────────────────────────────────────────────
  get cronometroAtivo(): boolean {
    return !!this.cronometroInicio;
  }

  get ticketId(): number | null {
    return this.formulario?.get('id')?.value ?? null;
  }

  iniciarCronometro(): void {
    const id = this.ticketId;
    if (!id) {
      this.snackBar.open('Salve o ticket antes de iniciar a contagem de tempo.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    this.ticketService.iniciarCronometro(id).subscribe({
      next: ticket => {
        this.aplicarTempos(ticket);
        const status = this.descStatus(this.formulario.getRawValue().status);
        this.registrarInteracao(`▶️ Contagem de tempo iniciada. Status atual: ${status}.`);
        this.snackBar.open('Contagem iniciada.', 'OK', { duration: 2000 });
      },
      error: () => this.snackBar.open('Erro ao iniciar a contagem.', 'ERRO', { duration: 3000 })
    });
  }

  pararCronometro(): void {
    const id = this.ticketId;
    if (!id) {
      return;
    }
    this.ticketService.pararCronometro(id).subscribe({
      next: ticket => {
        this.aplicarTempos(ticket);
        const status = this.descStatus(this.formulario.getRawValue().status);
        this.registrarInteracao(`⏸️ Contagem de tempo pausada. Status atual: ${status}. Tempo total gasto: ${this.tempoGastoFormatado}.`);
        this.snackBar.open('Contagem pausada.', 'OK', { duration: 2000 });
      },
      error: () => this.snackBar.open('Erro ao parar a contagem.', 'ERRO', { duration: 3000 })
    });
  }

  private aplicarTempos(ticket: any): void {
    this.tempoGastoSegundos = ticket.tempoGastoSegundos ?? 0;
    this.cronometroInicio = ticket.cronometroInicio ? new Date(ticket.cronometroInicio) : null;
    const horas = this.minutosParaHoras(ticket.tempoEstimadoMinutos);
    this.formulario.get('tempoEstimadoHoras')?.setValue(horas, { emitEvent: false });
    if (this.cronometroInicio) {
      this.iniciarTick();
    } else {
      this.pararTick();
    }
  }

  calcularTempoEstimadoMinutos(): number | null {
    const horas = Number(this.formulario?.get('tempoEstimadoHoras')?.value);
    return horas && horas > 0 ? Math.round(horas * 60) : null;
  }

  private minutosParaHoras(minutos: number | null | undefined): number | null {
    if (minutos == null || minutos <= 0) {
      return null;
    }
    return Math.round((minutos / 60) * 100) / 100;
  }

  get tempoGastoTotalSegundos(): number {
    let total = this.tempoGastoSegundos || 0;
    if (this.cronometroInicio) {
      total += Math.max(0, Math.floor((this.agora - this.cronometroInicio.getTime()) / 1000));
    }
    return total;
  }

  get tempoGastoFormatado(): string {
    return this.formatarSegundos(this.tempoGastoTotalSegundos);
  }

  get tempoEstimadoFormatado(): string {
    const minutos = this.calcularTempoEstimadoMinutos();
    return minutos != null ? this.formatarSegundos(minutos * 60, true) : '—';
  }

  get excedeuEstimativa(): boolean {
    const minutos = this.calcularTempoEstimadoMinutos();
    return minutos != null && this.tempoGastoTotalSegundos > minutos * 60;
  }

  private formatarSegundos(segundos: number, ocultarSegundos = false): string {
    const s = Math.max(0, Math.floor(segundos));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const seg = s % 60;
    if (ocultarSegundos || !this.cronometroAtivo) {
      const partes: string[] = [];
      if (h > 0) { partes.push(`${h}h`); }
      partes.push(`${m}min`);
      return partes.join(' ');
    }
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(seg)}`;
  }

  private iniciarTick(): void {
    this.pararTick();
    this.agora = Date.now();
    this.tickHandle = setInterval(() => this.agora = Date.now(), 1000);
  }

  private pararTick(): void {
    if (this.tickHandle) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  voltarConsulta() {
    this.router.navigate(['/ticket/consulta']);
  }

  selecionarArquivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.adicionarArquivos(input.files);
      input.value = '';
    }
  }

  soltarArquivos(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (event.dataTransfer?.files) {
      this.adicionarArquivos(event.dataTransfer.files);
    }
  }

  arrastarSobre(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  sairDoArrasto(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  removerArquivo(indice: number): void {
    this.uploadedFiles.splice(indice, 1);
    this.base64Files.splice(indice, 1);
  }

  baixarArquivo(event: { file: UploadedFile; index: number }): void {
    const link = document.createElement('a');
    link.href = this.base64Files[event.index].base64;
    link.download = event.file.name;
    link.click();
  }

  buscarUsuariosAtribuicao() {
    this.ticketService.buscarUsuariosAtribuicao().subscribe({
      next: (response) => this.atribuicoes = response,
    });
  }

  consultarParceiros(): void {
    this.clientService.listarCliente().subscribe(parceiros => {
      this.parceiros = parceiros;
    });
  }

  definirDataPrevisaoEntrega(): void {
    const categoria = this.formulario.get('tipoTicket')?.value;
    const prioridade = this.formulario.get('prioridade')?.value;
    if (!categoria || !prioridade) {
      this.formulario.get('dataPrevisaoEntrega')?.reset();
      return;
    }
    const dias = this.PRAZOS[categoria]?.[prioridade];
    if (!dias) {
      this.formulario.get('dataPrevisaoEntrega')?.reset();
      return;
    }
    const hoje = new Date();
    const dataPrevisao = new Date(hoje);
    dataPrevisao.setDate(hoje.getDate() + dias);
    this.formulario.get('dataPrevisaoEntrega')?.setValue(dataPrevisao);
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    const tamanhoMaximoBytes = 25 * 1024 * 1024;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > tamanhoMaximoBytes) {
        this.snackBar.open(`"${file.name}" excede o limite de 25 MB.`, 'Fechar', { duration: 3500 });
        continue;
      }
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagensSelecionadas.push({ nome: file.name, base64: e.target.result });
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  }

  removerImagem(index: number) {
    this.imagensSelecionadas.splice(index, 1);
  }

  adicionarComentario() {
    const ticketId = this.formulario.get('id')?.value;
    if (!ticketId) {
      return;
    }
    if (!this.novoComentario?.trim() && this.imagensSelecionadas.length === 0) {
      return;
    }
    const comentario = new NotaTicket(
      0,
      this.novoComentario,
      null,
      localStorage.getItem('usuarioLogado') || 'Usuário não identificado',
      ticketId,
      this.formulario.get('apresentarParceiroInteracao')?.value,
      [...this.imagensSelecionadas]
    );
    this.page = 0;
    this.ticketService.adicionarComentario(ticketId, comentario, this.page, this.size).subscribe({
      next: () => {
        this.novoComentario = '';
        this.imagensSelecionadas = [];
        this.carregarComentarios(ticketId, true);
        this.snackBar.open('Comentário publicado com sucesso!', 'Fechar', { duration: 3000 });
      },
      error: () => this.snackBar.open('Erro ao publicar comentário.', 'Fechar', { duration: 3000 })
    });
  }

  carregarMaisComentarios(): void {
    if (this.lastPage || this.loading) {
      return;
    }
    this.carregarComentarios(this.formulario.get('id')!.value);
  }

  carregarComentarios(ticketId: number, reset: boolean = false): void {
    if (this.loading) {
      return;
    }
    if (reset) {
      this.page = 0;
      this.lastPage = false;
      this.comentarios = [];
    }
    this.loading = true;
    this.ticketService.listarComentarios(ticketId, this.page, this.size).subscribe({
      next: (resposta) => {
        const novosComentarios: NotaTicket[] = resposta.content;
        this.comentarios = reset ? novosComentarios : [...this.comentarios, ...novosComentarios];
        this.lastPage = resposta.last;
        this.page++;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  private adicionarArquivos(listaDeArquivos: FileList): void {
    const tamanhoMaximoBytes = 25 * 1024 * 1024;
    Array.from(listaDeArquivos).forEach(arquivo => {
      if (arquivo.size > tamanhoMaximoBytes) {
        this.snackBar.open(`"${arquivo.name}" excede o limite de 25 MB.`, 'Fechar', { duration: 3500 });
        return;
      }
      this.convertToBase64(arquivo);
    });
  }

  private convertToBase64(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      this.base64Files.push({ nome: file.name, base64: base64String });
      this.uploadedFiles.push({
        name: file.name,
        preview: this.sanitizer.bypassSecurityTrustUrl(base64String),
        file
      });
    };
    reader.readAsDataURL(file);
  }

  private readonly PRAZOS: Record<string, Record<string, number>> = {
    CORRECAO_ERRO: { BASICA: 7, MEDIA: 5, ALTA: 2 },
    DEMANDA_TECNICA: { BASICA: 5, MEDIA: 2, ALTA: 1 },
    MELHORIA_SUGESTAO: { BASICA: 30, MEDIA: 25, ALTA: 20 },
    ATENDIMENTO_REALIZADO_CLIENTE: { BASICA: 5, MEDIA: 2, ALTA: 1 }
  };
}
